import React, { useEffect, useState } from 'react'
import { Col, Input, Modal, Row, Select, Switch, Typography, Upload, UploadFile } from 'antd'
import { LabeledValue } from 'antd/lib/select'
import type { RcFile, UploadProps } from 'antd/lib/upload'
import { useFormik } from 'formik'
import { AxiosError } from 'axios'
import { DocumentDownload, Image } from 'iconsax-react'
import { NumericFormat } from 'react-number-format'
import { IconCancel } from 'components/icons'
import Card from 'components/card/Card'
import Button from 'components/button/Button'
import RichTextEditor from 'components/form/RichTextEditor'
import {
  AdminsApi,
  AdminsEcommerceCreateProductRequest,
  Configuration,
  EcommerceProductDimensionMeasurementEnum,
  EcommerceProductStatusEnum,
  EcommerceProductUnitOfMeasurementEnum,
} from 'generated/api'
import { axiosDefaultConfig } from 'constants/axios'
import {
  DropDownEcommerceProductDimensionMeasurement,
  DropDownEcommerceProductUnitOfMeasurement,
} from 'constants/dropdown'
import { validationSchemaFormProduct } from 'helpers/validation'
import { classNameInput } from 'helpers/formik/classNameForm'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

interface Props {
  isOpen: boolean
  isLoading: boolean
  stateForm: 'CREATE' | 'EDIT'
  categories: LabeledValue[]
  initialForm: AdminsEcommerceCreateProductRequest
  onSubmit: (val: AdminsEcommerceCreateProductRequest) => void
  onClose: () => void
}

export default function ModalFormProduct(props: Props) {
  const formik = useFormik({
    initialValues: props.initialForm,
    enableReinitialize: true,
    validateOnMount: true,
    validationSchema: validationSchemaFormProduct,
    onSubmit: async values => {
      props.onSubmit(values)
    },
  })

  const { values, errors, touched, setFieldValue, setFieldTouched, setFieldError, handleSubmit } =
    formik

  const [fileList, setFileList] = useState<UploadFile[]>([])

  useEffect(() => {
    if (values.imageURL) {
      setFileList([
        {
          uid: '-1',
          name: values.imageURL?.split('/').pop() as string,
          status: 'done',
          url: values.imageURL,
        },
      ])
    }
  }, [values.imageURL])

  const imageProps: UploadProps = {
    maxCount: 1,
    multiple: false,
    showUploadList: {
      showRemoveIcon: false,
    },
    fileList: fileList,
    listType: 'picture',
    iconRender: () => {
      return <Image size={24} />
    },
    beforeUpload: async (file: RcFile) => {
      const imageType = ['image/png', 'image/jpeg', 'image/jpg']
      const maxFileSize = 2 * 1024 * 1024 // 2MB
      const minFileSize = 1024
      const isImage = imageType.includes(file.type)
      const isSizeMinAllowed = minFileSize < file.size
      const isSizeMaxAllowed = maxFileSize > file.size

      if (!isImage) {
        await setFieldTouched('imageURL', true)
        setFieldError(
          'imageURL',
          `${file.name} tidak valid. Hanya menerima file dengan format PNG, JPG, JPEG`,
        )
        return false
      }

      if (!isSizeMinAllowed) {
        await setFieldTouched('imageURL', true)
        setFieldError('imageURL', `${file.name} minimal ukuran lebih dari 1KB`)
        return false
      }

      if (!isSizeMaxAllowed) {
        await setFieldTouched('imageURL', true)
        setFieldError('imageURL', `${file.name} melebihi ukuran maksimal 2MB`)
        return false
      }

      return (isImage && isSizeMaxAllowed && isSizeMinAllowed) || Upload.LIST_IGNORE
    },
    customRequest: async ({ onError, onSuccess, file }) => {
      const uploadFile = file as RcFile
      try {
        setFileList([
          { uid: uploadFile.uid, name: uploadFile.name, status: 'uploading', percent: 0 },
        ])
        await setFieldValue('imageURL', '')

        const { data } = await adminsApi.adminsEcommerceUploadProductImage('', uploadFile, {
          onUploadProgress: function (progressEvent) {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              )
              setFileList([
                {
                  uid: uploadFile.uid,
                  name: uploadFile.name,
                  status: 'uploading',
                  percent: percentCompleted,
                },
              ])
            }
          },
        })
        setFileList([
          { uid: uploadFile.uid, name: uploadFile.name, status: 'done', url: data.imageURL },
        ])
        onSuccess?.(data)
        await setFieldValue('imageURL', data.imageURL)
      } catch (err) {
        const error = err as AxiosError
        onError?.(error)
        await setFieldValue('imageURL', '')
        setFileList([
          {
            uid: uploadFile.uid,
            name: uploadFile.name,
            status: 'error',
          },
        ])
      }
    },
  }

  const title = props.stateForm === 'CREATE' ? 'Tambah Produk' : 'Edit Produk'

  return (
    <Modal
      data-testid="modal_product_form"
      centered
      closable={!props.isOpen}
      onCancel={props.onClose}
      title={title}
      open={props.isOpen}
      footer={null}
      className="modal-admin pt-5"
      width={650}
      closeIcon={<IconCancel strokeWidth="2" color="black" />}
    >
      <Card
        loading={props.isLoading}
        dataTestId="card_products_form"
        spinnerTestId="loader_product_form"
        className="rounded-xl p-0"
      >
        <div className="flex flex-col gap-2">
          <div>
            <div className="mb-2">
              <Typography.Text className="text-xs font-bold">SKU</Typography.Text>
              <Typography.Text className="text-xs font-bold text-[#FF4D4D]">*</Typography.Text>
            </div>
            <Input
              placeholder="Contoh: FER-KP-HRM-1L"
              data-testid="input_sku"
              className={classNameInput('sku', formik)}
              value={values.sku}
              onChange={({ target: { value } }) => setFieldValue('sku', value)}
              status={errors.sku && touched.sku ? 'error' : ''}
            />
            {errors.sku && touched.sku ? (
              <Typography.Text
                className="text-[#FF4D4D] text-xs font-normal"
                data-testid="error_sku"
              >
                {errors.sku}
              </Typography.Text>
            ) : null}
          </div>

          <div>
            <div className="mb-2">
              <Typography.Text className="text-xs font-bold">Kategori</Typography.Text>
              <Typography.Text className="text-xs font-bold text-[#FF4D4D]">*</Typography.Text>
            </div>
            <Select
              placeholder="Pilih Kategori"
              data-testid="select_category"
              className="selector h-full w-full"
              value={values.categoryId || null}
              onChange={value => setFieldValue('categoryId', value)}
              status={errors.categoryId && touched.categoryId ? 'error' : ''}
              virtual={false}
            >
              {props.categories.map(category => {
                return (
                  <Select.Option
                    data-testid={'select_category_' + category.value}
                    key={category.value}
                    value={category.value}
                  >
                    {category.label}
                  </Select.Option>
                )
              })}
            </Select>
            {errors.categoryId && touched.categoryId ? (
              <Typography.Text
                className="text-[#FF4D4D] text-xs font-normal"
                data-testid="error_category"
              >
                {errors.categoryId}
              </Typography.Text>
            ) : null}
          </div>

          <div>
            <div className="mb-2">
              <Typography.Text className="text-xs font-bold">Nama Produk</Typography.Text>
              <Typography.Text className="text-xs font-bold text-[#FF4D4D]">*</Typography.Text>
            </div>
            <Input
              placeholder="Contoh: Pabrik Buatan Satu"
              data-testid="input_name"
              className={classNameInput('name', formik)}
              value={values.name}
              onChange={({ target: { value } }) => setFieldValue('name', value)}
              status={errors.name && touched.name ? 'error' : ''}
            />
            {errors.name && touched.name ? (
              <Typography.Text
                className="text-[#FF4D4D] text-xs font-normal"
                data-testid="error_name"
              >
                {errors.name}
              </Typography.Text>
            ) : null}
          </div>

          <div>
            <div className="mb-2">
              <Typography.Text className="text-xs font-bold">Deskripsi Singkat</Typography.Text>
            </div>
            <Input.TextArea
              placeholder="Contoh: Produk Pupuk Terbaik"
              data-testid="input_description"
              className={classNameInput('description', formik)}
              value={values.description}
              onChange={({ target: { value } }) => setFieldValue('description', value)}
              rows={2}
            />
          </div>

          <div>
            <div className="mb-2">
              <Typography.Text className="text-xs font-bold">Detail Produk</Typography.Text>
            </div>
            <RichTextEditor
              value={values.detailDescription || ''}
              onChange={val => setFieldValue('detailDescription', val)}
            />
          </div>

          <div>
            <Row gutter={16}>
              <Col span={12}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">Satuan Ukuran</Typography.Text>
                    <Typography.Text className="text-xs font-bold text-[#FF4D4D]">
                      *
                    </Typography.Text>
                  </div>
                  <Select
                    placeholder="Pilih Satuan"
                    data-testid="select_unitOfMeasurement"
                    className="selector h-full w-full"
                    value={values.unitOfMeasurement || null}
                    onChange={(val: EcommerceProductUnitOfMeasurementEnum) =>
                      setFieldValue('unitOfMeasurement', val)
                    }
                    status={errors.unitOfMeasurement && touched.unitOfMeasurement ? 'error' : ''}
                    virtual={false}
                  >
                    {DropDownEcommerceProductUnitOfMeasurement.map(list => {
                      return (
                        <Select.Option
                          data-testid={'select_unitOfMeasurement_' + list.value}
                          key={list.value}
                          value={list.value}
                        >
                          {list.label}
                        </Select.Option>
                      )
                    })}
                  </Select>
                  {errors.unitOfMeasurement && touched.unitOfMeasurement ? (
                    <Typography.Text
                      className="text-[#FF4D4D] text-xs font-normal"
                      data-testid="error_unitOfMeasurement"
                    >
                      {errors.unitOfMeasurement}
                    </Typography.Text>
                  ) : null}
                </div>
              </Col>
              <Col span={12}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">Ukuran</Typography.Text>
                    <Typography.Text className="text-xs font-bold text-[#FF4D4D]">
                      *
                    </Typography.Text>
                  </div>
                  <NumericFormat
                    placeholder="Contoh: 10"
                    data-testid="input_productWeight"
                    className={classNameInput('productWeight', formik)}
                    value={values.productWeight || null}
                    onValueChange={val => setFieldValue('productWeight', val.floatValue ?? '')}
                    status={errors.productWeight && touched.productWeight ? 'error' : ''}
                    customInput={Input}
                    allowNegative={false}
                    decimalScale={0}
                  />
                  {errors.productWeight && touched.productWeight ? (
                    <Typography.Text
                      className="text-[#FF4D4D] text-xs font-normal"
                      data-testid="error_productWeight"
                    >
                      {errors.productWeight}
                    </Typography.Text>
                  ) : null}
                </div>
              </Col>
            </Row>
          </div>

          <div>
            <Row gutter={16}>
              <Col span={6}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">
                      Satuan Ukur Dimensi
                    </Typography.Text>
                  </div>
                  <Select
                    placeholder="Pilih Ukuran"
                    data-testid="select_dimensionMeasurement"
                    className="selector h-full w-full"
                    value={values.dimensionMeasurement || null}
                    onChange={(val: EcommerceProductDimensionMeasurementEnum) =>
                      setFieldValue('dimensionMeasurement', val)
                    }
                    status={
                      errors.dimensionMeasurement && touched.dimensionMeasurement ? 'error' : ''
                    }
                  >
                    {DropDownEcommerceProductDimensionMeasurement.map(list => {
                      return (
                        <Select.Option
                          data-testid={'select_dimensionMeasurement_' + list.value}
                          key={list.value}
                          value={list.value}
                        >
                          {list.label}
                        </Select.Option>
                      )
                    })}
                  </Select>
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">Dimensi Panjang</Typography.Text>
                  </div>
                  <NumericFormat
                    placeholder="Contoh: 10"
                    data-testid="input_dimensionLength"
                    className={classNameInput('dimensionLength', formik)}
                    value={values.dimensionLength || null}
                    onValueChange={val => setFieldValue('dimensionLength', val.floatValue ?? '')}
                    status={errors.dimensionLength && touched.dimensionLength ? 'error' : ''}
                    customInput={Input}
                    allowNegative={false}
                    decimalScale={0}
                  />
                  {errors.dimensionLength && touched.dimensionLength ? (
                    <Typography.Text
                      className="text-[#FF4D4D] text-xs font-normal"
                      data-testid="error_dimensionLength"
                    >
                      {errors.dimensionLength}
                    </Typography.Text>
                  ) : null}
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">Dimensi Lebar</Typography.Text>
                  </div>
                  <NumericFormat
                    placeholder="Contoh: 10"
                    data-testid="input_dimensionWidth"
                    className={classNameInput('dimensionWidth', formik)}
                    value={values.dimensionWidth || null}
                    onValueChange={val => setFieldValue('dimensionWidth', val.floatValue ?? '')}
                    status={errors.dimensionWidth && touched.dimensionWidth ? 'error' : ''}
                    customInput={Input}
                    allowNegative={false}
                    decimalScale={0}
                  />
                  {errors.dimensionWidth && touched.dimensionWidth ? (
                    <Typography.Text
                      className="text-[#FF4D4D] text-xs font-normal"
                      data-testid="error_dimensionWidth"
                    >
                      {errors.dimensionWidth}
                    </Typography.Text>
                  ) : null}
                </div>
              </Col>
              <Col span={6}>
                <div>
                  <div className="mb-2">
                    <Typography.Text className="text-xs font-bold">Dimensi Tinggi</Typography.Text>
                  </div>
                  <NumericFormat
                    placeholder="Contoh: 10"
                    data-testid="input_dimensionHeight"
                    className={classNameInput('dimensionHeight', formik)}
                    value={values.dimensionHeight || null}
                    onValueChange={val => setFieldValue('dimensionHeight', val.floatValue ?? '')}
                    status={errors.dimensionHeight && touched.dimensionHeight ? 'error' : ''}
                    customInput={Input}
                    allowNegative={false}
                    decimalScale={0}
                  />
                  {errors.dimensionHeight && touched.dimensionHeight ? (
                    <Typography.Text
                      className="text-[#FF4D4D] text-xs font-normal"
                      data-testid="error_dimensionWidth"
                    >
                      {errors.dimensionHeight}
                    </Typography.Text>
                  ) : null}
                </div>
              </Col>
            </Row>
          </div>

          <div>
            <Upload.Dragger data-testid="input-product-image" {...imageProps} className="pb-5">
              <div className="my-0 font-xs flex justify-center">
                <DocumentDownload color="#3C5337" />
                <span className="md:pl-3">
                  Tarik foto kesini atau klik <span className="text-[#3C7098]">Upload</span> untuk
                  upload foto
                </span>
              </div>
              <p className="mt-3 mb-0 font-xs text-[#858C95]">
                Format: PNG, JPG,JPEG. Maksimal file 2MB
              </p>
            </Upload.Dragger>
            {errors.imageURL && touched.imageURL ? (
              <Typography.Text
                className="text-[#FF4D4D] text-xs font-normal"
                data-testid="error_imageURL"
              >
                {errors.imageURL}
              </Typography.Text>
            ) : null}
          </div>

          <div>
            <Switch
              data-testid="switch_status"
              checked={values.status === EcommerceProductStatusEnum.Active}
              onChange={checked =>
                setFieldValue(
                  'status',
                  checked ? EcommerceProductStatusEnum.Active : EcommerceProductStatusEnum.Inactive,
                )
              }
            />
            <Typography.Text className="ml-2 text-sm text-[#202020] font-medium">
              {values.status === EcommerceProductStatusEnum.Active
                ? 'Produk Aktif'
                : 'Produk Tidak Aktif'}
            </Typography.Text>
          </div>

          <Button
            type="submit"
            loading={props.isLoading}
            block={true}
            onClick={handleSubmit}
            data-testid="btn_accept"
            className="my-3 pt-3 rounded-xl font-bold text-sm"
            iconPosition="right"
            label="SIMPAN"
            variant="solid-primary"
          />
        </div>
      </Card>
    </Modal>
  )
}
